'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { trackEvent } from '@/lib/track';
import { TrustedBy } from '@/components/trustedBy/trustedBy.comp';
import { BENCHMARKS } from '@/components/websiteAnalyzer/benchmarks';
import { extraRecommendations } from './v4ExtraRecs';
import {
  generateRecommendations,
  scoreReport,
  getFreeTool,
  freeToolPath,
  CATEGORY_LABELS,
  CATEGORY_METHODOLOGY,
  type ISiteSignals,
  type IRecommendation,
} from '@/components/websiteAnalyzer/recommendationRules';
import type { ISavedReport } from '@/components/websiteAnalyzer/reportStore';
import {
  StyledHero,
  StyledForm,
  StyledHint,
  StyledBody,
  StyledLoader,
  StyledSummary,
  StyledWarning,
  StyledShareBar,
  StyledScorecard,
  StyledDonut,
  StyledBars,
  StyledBar,
  StyledTrustBadges,
  StyledRecsHeader,
  StyledRecGrid,
  StyledRecCard,
  StyledWidgetGallery,
  StyledWidgetTile,
} from './WebsiteAnalyzerV4.styles';

export interface IWidgetMeta {
  slug: string;
  name: string;
  logo: string;
}

interface IProps {
  widgets: Record<string, IWidgetMeta>;
  // Set on /website-analyzer/report/<id>: render this saved result instead of an empty form.
  initialReport?: ISavedReport;
}

type ShareState = 'idle' | 'copied' | 'manual';

const reportLink = (reportId: string) => `${window.location.origin}/website-analyzer/report/${reportId}`;

// UTC so the server render and the browser agree on the day.
const formatScanDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

type Status = 'poor' | 'fair' | 'good';

const statusOf = (score: number): Status => (score >= 75 ? 'good' : score >= 50 ? 'fair' : 'poor');
const verdictOf = (score: number): string =>
  score >= 75 ? 'Looking strong' : score >= 50 ? 'Room to grow' : 'Needs attention';

const impactOf = (priority: number): 'high' | 'medium' | 'low' =>
  priority >= 80 ? 'high' : priority >= 55 ? 'medium' : 'low';
const impactDots = (impact: 'high' | 'medium' | 'low') =>
  impact === 'high' ? 3 : impact === 'medium' ? 2 : 1;

const heroUrl = (slug: string) => `https://website-assets.commoninja.com/heroes/${slug}.webp`;

// A short, unique id per analysis run so one visitor's path (analyze -> impression
// -> widget click -> free-tool click) can be stitched together in Mixpanel.
const newAnalysisId = () =>
  `wa_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// Tag the analyzer's outbound links so a widget build or signup downstream can be
// traced back to a specific analysis. NOTE: these are internal commoninja.com links;
// if GA4 is the marketing-site analytics, utm_* keys can self-referral / overwrite the
// true session source. Centralized here so swapping utm_* for a neutral `ref` param is
// a one-line change if the analytics owner flags it.
const withTracking = (path: string, content: string, aid: string) => {
  const params = new URLSearchParams({
    utm_source: 'website-analyzer',
    utm_medium: 'referral',
    utm_campaign: 'widget-rec',
    utm_content: content,
  });
  if (aid) params.set('aid', aid);
  return `${path}${path.includes('?') ? '&' : '?'}${params.toString()}`;
};

// Where the visitor arrived from, so each analysis can be attributed to the exact
// page that sent them: which blog post's embed, which free-tool CTA, or direct.
// document.referrer is the previous page; the embed/CTA is a same-origin GET form,
// so on commoninja.com the referrer carries the full path incl. the blog slug
// (rel="noopener" only blocks window.opener, not the referrer). An explicit ?src=
// always wins when present, so a future embed can hard-code its origin if a browser
// strips the referrer.
const computeEntryPoint = (): { entryPoint: string; referrer: string } => {
  if (typeof window === 'undefined') return { entryPoint: 'unknown', referrer: '' };
  const explicit = new URLSearchParams(window.location.search).get('src');
  const ref = document.referrer || '';
  if (explicit) return { entryPoint: explicit, referrer: ref };
  if (!ref) return { entryPoint: 'direct', referrer: '' };
  try {
    const u = new URL(ref);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'commoninja.com') {
      const path = u.pathname.replace(/\/+$/, '') || '/';
      if (path.startsWith('/blog/')) return { entryPoint: `blog:${path.slice(6)}`, referrer: ref };
      if (path.startsWith('/free-tools/'))
        return { entryPoint: `free-tool:${path.slice(12)}`, referrer: ref };
      if (path === '/website-analyzer') return { entryPoint: 'analyzer-self', referrer: ref };
      return { entryPoint: `site:${path}`, referrer: ref };
    }
    return { entryPoint: `external:${host}`, referrer: ref };
  } catch {
    return { entryPoint: 'unknown', referrer: ref };
  }
};

// Send an analyzer event to BOTH sinks: Mixpanel (existing) and our own DB endpoint
// (so reports can be pulled straight from our MongoDB, no third-party read access).
// The POST is fire-and-forget with keepalive so it survives the navigation when a
// widget/tool link is clicked; the endpoint itself drops non-prod traffic.
const record = (name: string, props: Record<string, unknown>) => {
  trackEvent(name, props);
  if (typeof window === 'undefined') return;
  try {
    fetch('/api/analyze/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, props }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics must never break the page */
  }
};

// Donut geometry.
const R = 88;
const CIRC = 2 * Math.PI * R;

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="12" y1="11" x2="12" y2="16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="7.5" r="1.3" fill="currentColor" />
  </svg>
);

export const WebsiteAnalyzerV4 = ({ widgets, initialReport }: IProps) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Guard against the native form submit firing before React hydrates (which would
  // reload the page and wipe the results). Enabled once mounted on the client.
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);

  const [signals, setSignals] = useState<ISiteSignals | null>(initialReport?.signals ?? null);
  const [summary, setSummary] = useState(initialReport?.summary ?? '');
  const [warning, setWarning] = useState(initialReport?.warning ?? '');
  const [analysisId, setAnalysisId] = useState('');

  // Share link for the result on screen. `sharedAt` is set only while showing a saved
  // report someone sent, and clears as soon as the visitor runs their own scan.
  const [reportId, setReportId] = useState(initialReport?.reportId ?? '');
  const [sharedAt, setSharedAt] = useState(initialReport?.createdAt ?? '');
  const [shareState, setShareState] = useState<ShareState>('idle');

  // Score reveal animation (V2).
  const [shown, setShown] = useState(false);
  const [displayOverall, setDisplayOverall] = useState(0);
  // Hero-image fallback tracking (V3).
  const [failedHeroes, setFailedHeroes] = useState<Record<string, boolean>>({});

  const resultsRef = useRef<HTMLDivElement>(null);

  // Capture the entry point once (referrer is stable after the initial page load)
  // and reuse it on every event so an analysis is attributed to its source page.
  const entryRef = useRef<{ entryPoint: string; referrer: string } | null>(null);
  const entry = () => {
    if (!entryRef.current) {
      const computed = computeEntryPoint();
      // A scan started from a shared report is credited to the share, whatever sent
      // the visitor to it (a chat app usually passes no referrer at all).
      entryRef.current = initialReport ? { ...computed, entryPoint: 'shared-report' } : computed;
    }
    return entryRef.current;
  };

  const report = useMemo(() => {
    if (!signals) return null;
    const scored = scoreReport(signals);
    const recs = [...generateRecommendations(signals), ...extraRecommendations(signals)].sort(
      (a, b) => b.priority - a.priority
    );
    // Dedupe widget tiles across recommendations. The same widget can legitimately
    // address several gaps (the FAQ widget helps the conversion FAQ gap, LLM SEO, and
    // rich results), but showing the identical tile 2-3 times reads as a bug. Walking
    // in priority order, the highest-priority card that references a widget keeps it
    // and later cards drop it, so each widget appears exactly once. This mirrors the
    // Widget Impression effect, which already dedupes globally via its `seen` Set.
    const claimed = new Set<string>();
    const deduped = recs.map((rec) => ({
      ...rec,
      widgetSlugs: rec.widgetSlugs.filter((slug) =>
        claimed.has(slug) ? false : (claimed.add(slug), true)
      ),
    }));
    return { ...scored, recs: deduped };
  }, [signals]);

  useEffect(() => {
    if (!report) return;
    setShown(false);
    setDisplayOverall(0);
    const raf = requestAnimationFrame(() => setShown(true));

    const target = report.overall;
    const duration = 1300;
    let start: number | null = null;
    let frame = 0;
    const tick = (t: number) => {
      if (start === null) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayOverall(Math.round(eased * target));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(frame);
    };
  }, [report]);

  // Fire one impression per distinct recommended widget so we can compute a true
  // click-through rate per widget (clicks / impressions), not just raw clicks.
  useEffect(() => {
    if (!report || !analysisId) return;
    const seen = new Set<string>();
    report.recs.forEach((rec) =>
      rec.widgetSlugs.forEach((slug) => {
        if (widgets[slug] && !seen.has(slug)) {
          seen.add(slug);
          record('Website Analyzer V4 - Widget Impression', {
            widget: slug,
            analysisId,
            url: signals?.finalUrl,
            overallScore: report.overall,
            ...entry(),
          });
        }
      })
    );
    // Impressions belong to one analysis; re-fire only when a new report lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, analysisId]);

  const reset = () => {
    setError('');
    setSignals(null);
    setSummary('');
    setWarning('');
    setReportId('');
    setSharedAt('');
    setShareState('idle');
  };

  const runAnalysis = (target: string, source: 'form' | 'embed' = 'form') => {
    const clean = target.trim();
    if (loading) return;
    if (!clean) {
      setError('Please enter your website URL.');
      return;
    }
    reset();
    setLoading(true);
    // Local id used across this run's events; state copy drives links + impressions.
    const aid = newAnalysisId();
    setAnalysisId(aid);
    record('Website Analyzer V4 - Analyze', { url: clean, source, analysisId: aid, ...entry() });

    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);

    fetch('/api/analyze/page', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: clean }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) {
          setError(data.message || 'We could not analyze that URL.');
          record('Website Analyzer V4 - Analyze Fail', {
            url: clean,
            source,
            analysisId: aid,
            reason: data.message || 'unknown',
            ...entry(),
          });
          return;
        }
        setSignals(data.signals);
        setSummary(data.summary || '');
        setWarning(data.warning || '');
        setReportId(data.reportId || '');
        // Score inline so the success event carries the numbers without waiting on
        // the render-time memo (mirrors the `report` useMemo above).
        const scored = scoreReport(data.signals);
        const recCount =
          generateRecommendations(data.signals).length +
          extraRecommendations(data.signals).length;
        record('Website Analyzer V4 - Analyze Success', {
          url: clean,
          source,
          analysisId: aid,
          overallScore: scored.overall,
          recCount,
          ...entry(),
        });
      })
      .catch(() => {
        setError('Something went wrong. Please try again.');
        record('Website Analyzer V4 - Analyze Fail', {
          url: clean,
          source,
          analysisId: aid,
          reason: 'network_error',
          ...entry(),
        });
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runAnalysis(url);
  };

  // Entry embed: when a blog snippet sends a reader here with ?url=..., prefill
  // the field and run the analysis automatically so they land straight on results.
  useEffect(() => {
    if (initialReport) return;
    const incoming = new URLSearchParams(window.location.search).get('url');
    if (!incoming || !incoming.trim()) return;
    setUrl(incoming.trim());
    runAnalysis(incoming, 'embed');
    // run once on mount; runAnalysis is stable enough for this one-shot trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opening a shared report counts as a view; it gets its own analysisId so the
  // impressions and clicks that follow thread back to this view.
  useEffect(() => {
    if (!initialReport) return;
    // Land the recipient on the report itself, not on the empty scan form above it.
    resultsRef.current?.scrollIntoView({ block: 'start' });
    const aid = newAnalysisId();
    setAnalysisId(aid);
    record('Website Analyzer V4 - Report View', {
      reportId: initialReport.reportId,
      url: initialReport.signals.finalUrl || initialReport.signals.url,
      analysisId: aid,
      overallScore: scoreReport(initialReport.signals).overall,
      ...entry(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phones get their own share sheet (WhatsApp, Mail...); desktops copy to the
  // clipboard. If both are unavailable the link is shown so it can be copied by hand.
  const handleShare = async () => {
    if (!reportId) return;
    const link = reportLink(reportId);
    const track = (method: string) =>
      record('Website Analyzer V4 - Share Click', {
        reportId,
        method,
        url: signals?.finalUrl,
        analysisId,
        overallScore: report?.overall,
        ...entry(),
      });

    const isTouch = window.matchMedia?.('(pointer: coarse)').matches;
    if (isTouch && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: 'My website report', url: link });
        track('native');
        return;
      } catch (e) {
        // The visitor closed the share sheet: nothing to do.
        if ((e as Error).name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(link);
      setShareState('copied');
      track('copy');
      setTimeout(() => setShareState((s) => (s === 'copied' ? 'idle' : s)), 2500);
    } catch {
      setShareState('manual');
      track('manual');
    }
  };

  const renderStat = (statId?: string) => {
    const stat = statId ? BENCHMARKS[statId] : undefined;
    if (!stat) return null;
    return (
      <div className="stat">
        <div className="num">{stat.number}</div>
        <div className="body">
          <div className="claim">{stat.claim}</div>
          <div className="src">
            Source:{' '}
            <a href={stat.sourceUrl} target="_blank" rel="noopener noreferrer nofollow">
              {stat.sourceName}
            </a>{' '}
            ({stat.year})
          </div>
        </div>
      </div>
    );
  };

  const renderWidgetTile = (w: IWidgetMeta) => {
    const failed = failedHeroes[w.slug];
    return (
      <StyledWidgetTile
        key={w.slug}
        href={withTracking(`/widgets/${w.slug}`, w.slug, analysisId)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          record('Website Analyzer V4 - Widget Click', {
            widget: w.slug,
            url: signals?.finalUrl,
            analysisId,
            overallScore: report?.overall,
            ...entry(),
          })
        }
      >
        <div className="thumb">
          {failed ? (
            <div className="fallback">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              {w.logo && <img src={w.logo} alt={`${w.name} icon`} loading="lazy" />}
            </div>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={heroUrl(w.slug)}
              alt={`${w.name} widget preview`}
              loading="lazy"
              onError={() => setFailedHeroes((prev) => ({ ...prev, [w.slug]: true }))}
            />
          )}
        </div>
        <div className="caption">
          <span className="name">{w.name}</span>
          <span className="go">View →</span>
        </div>
      </StyledWidgetTile>
    );
  };

  const renderRec = (rec: IRecommendation) => {
    const impact = impactOf(rec.priority);
    const onDots = impactDots(impact);
    const widgetMetas = rec.widgetSlugs.map((s) => widgets[s]).filter(Boolean) as IWidgetMeta[];
    const tools = rec.freeToolSlugs.map((s) => getFreeTool(s)).filter(Boolean);

    return (
      <StyledRecCard key={rec.id} $impact={impact}>
        <div className="head">
          <span className="cat">{CATEGORY_LABELS[rec.category]}</span>
          <span className="impact">
            {impact} impact
            <span className="dots">
              {[0, 1, 2].map((i) => (
                <span key={i} className={`dot ${i < onDots ? 'on' : ''}`} />
              ))}
            </span>
          </span>
        </div>

        <h3>{rec.title}</h3>
        <p className="finding">{rec.finding}</p>

        {renderStat(rec.statId)}

        {widgetMetas.length > 0 && (
          <>
            <div className="gallery-label">
              {widgetMetas.length === 1 ? 'The widget that fixes this' : 'Widgets that fix this'}
            </div>
            <StyledWidgetGallery>{widgetMetas.map(renderWidgetTile)}</StyledWidgetGallery>
          </>
        )}

        {tools.length > 0 && (
          <div className="tools">
            <span className="tools-label">Put numbers to it with a free tool</span>
            {tools.map(
              (t) =>
                t && (
                  <Link
                    key={t.slug}
                    href={withTracking(freeToolPath(t), t.slug, analysisId)}
                    onClick={() =>
                      record('Website Analyzer V4 - Free Tool Click', {
                        tool: t.slug,
                        url: signals?.finalUrl,
                        analysisId,
                        overallScore: report?.overall,
                        ...entry(),
                      })
                    }
                  >
                    {t.name} →
                  </Link>
                )
            )}
          </div>
        )}
      </StyledRecCard>
    );
  };

  return (
    <>
      <StyledHero>
        <h1>
          See your score, and the tools to <span className="highlight">raise it</span>.
        </h1>
        <p className="sub">
          Enter your URL for an instant, visual health check. See your scores animated to life, plus
          the real widgets that close each gap, each backed by real data.
        </p>
        <StyledForm onSubmit={handleSubmit}>
          <div className="input-row">
            <input
              type="text"
              inputMode="url"
              placeholder="yourwebsite.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <button type="submit" disabled={loading || !ready}>
              {loading ? 'Analyzing…' : 'Analyze My Site'}
            </button>
          </div>
          {error && <p className="error">{error}</p>}
        </StyledForm>

        <StyledTrustBadges>
          <span className="badge">
            <strong>500K+</strong> websites trust Common Ninja
          </span>
          <a
            className="trustpilot"
            href="https://www.trustpilot.com/review/commoninja.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <Image
              src="/assets/trustpilot.png"
              alt="Common Ninja Trustpilot rating: 4.3 out of 5"
              width={215}
              height={30}
              title="4.3 stars out of 5"
            />
          </a>
        </StyledTrustBadges>

        {!signals && !loading && <StyledHint>Free · No sign-up · Results in seconds</StyledHint>}
      </StyledHero>

      <TrustedBy />

      <StyledBody ref={resultsRef}>
        {loading && !signals && (
          <StyledLoader>
            <div className="ring" />
            <p>
              Analyzing <span className="url">{url}</span> …
            </p>
          </StyledLoader>
        )}

        {report && signals && (
          <>
            <StyledSummary>
              {sharedAt ? 'Saved report for ' : 'Results for '}
              <strong>{signals.finalUrl || signals.url}</strong>
              {sharedAt ? `, scanned ${formatScanDate(sharedAt)}` : ''}
              {summary ? `. ${summary}` : ''}
            </StyledSummary>
            {warning && <StyledWarning>{warning}</StyledWarning>}

            {reportId && (
              <StyledShareBar>
                <button type="button" onClick={handleShare}>
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path
                      d="M10 14a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5M14 10a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  {shareState === 'copied' ? 'Link copied' : 'Share this report'}
                </button>
                <span className="status" aria-live="polite">
                  {shareState === 'manual' ? (
                    <input
                      readOnly
                      value={reportLink(reportId)}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label="Report link"
                    />
                  ) : sharedAt ? (
                    'Want your own score? Enter your URL above.'
                  ) : (
                    'Anyone with the link sees this exact report.'
                  )}
                </span>
              </StyledShareBar>
            )}

            <StyledScorecard>
              <StyledDonut $status={statusOf(report.overall)}>
                <div className="donut">
                  <svg viewBox="0 0 200 200">
                    <circle className="track" cx="100" cy="100" r={R} fill="none" strokeWidth="16" />
                    <circle
                      className="value"
                      cx="100"
                      cy="100"
                      r={R}
                      fill="none"
                      strokeWidth="16"
                      strokeDasharray={CIRC}
                      strokeDashoffset={shown ? CIRC * (1 - report.overall / 100) : CIRC}
                    />
                  </svg>
                  <div className="center">
                    <span className="num">{displayOverall}</span>
                    <span className="denom">/ 100</span>
                  </div>
                </div>
                <div className="label">Overall Score</div>
                <div className="verdict">{verdictOf(report.overall)}</div>
              </StyledDonut>

              <StyledBars>
                {report.categories.map((c) => (
                  <StyledBar key={c.key} $status={c.status} $pct={c.score} $shown={shown}>
                    <div className="top">
                      <span className="name">
                        {CATEGORY_LABELS[c.key]}
                        <span
                          className="info"
                          tabIndex={0}
                          role="button"
                          aria-label={`How the ${CATEGORY_LABELS[c.key]} score is calculated`}
                        >
                          <InfoIcon />
                          <span className="tooltip" role="tooltip">
                            {CATEGORY_METHODOLOGY[c.key]}
                          </span>
                        </span>
                      </span>
                      <span className="val">{c.score}</span>
                    </div>
                    <div className="track">
                      <div className="fill" />
                    </div>
                  </StyledBar>
                ))}
              </StyledBars>
            </StyledScorecard>

            <StyledRecsHeader>
              <h2>
                {report.recs.length} {report.recs.length === 1 ? 'way' : 'ways'} to improve your site
              </h2>
              <p>Ranked by impact, matched to a real widget you can see, and backed by real data.</p>
            </StyledRecsHeader>

            <StyledRecGrid>{report.recs.map(renderRec)}</StyledRecGrid>
          </>
        )}
      </StyledBody>
    </>
  );
};
